import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "whatisinside.install.dismissed.v1";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => setShow(false);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show a manual hint after a short delay
    let t;
    if (isIOS()) {
      t = setTimeout(() => setShow(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
      setShow(false);
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="fixed z-50 left-4 right-4 bottom-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm"
          data-testid="install-prompt"
        >
          <div className="surface-elev rounded-2xl p-4 border border-white/10 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)]">
            <button
              onClick={dismiss}
              data-testid="install-dismiss"
              aria-label="Dismiss"
              className="absolute top-3 right-3 text-[color:var(--text-3)] hover:text-[color:var(--text)] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-[color:var(--lime)] flex items-center justify-center flex-shrink-0">
                <Download className="h-5 w-5 text-[#07130a]" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 pr-5">
                <div className="font-display font-semibold text-[color:var(--text)]">Install WhatIsInside</div>
                <p className="text-xs text-[color:var(--text-2)] mt-0.5 leading-relaxed">
                  Add it to your home screen for one-tap, full-screen ingredient checks — no app store needed.
                </p>
              </div>
            </div>

            {iosHint ? (
              <div
                className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-[color:var(--text-2)] leading-relaxed"
                data-testid="install-ios-hint"
              >
                Tap the <Share className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> <b>Share</b> button in Safari, then choose
                <span className="inline-flex items-center gap-1 mx-1 text-[color:var(--text)]">
                  <Plus className="h-3.5 w-3.5" /> <b>Add to Home Screen</b>
                </span>.
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={install}
                  data-testid="install-accept"
                  className="lime-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 flex-1 justify-center"
                >
                  <Download className="h-4 w-4" /> Install
                </button>
                <button
                  onClick={dismiss}
                  data-testid="install-later"
                  className="ghost-btn rounded-lg px-4 py-2 text-sm"
                >
                  Not now
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
